import { useEffect, useRef, useState } from "react";
import { ArrowDown, ArrowUp, Columns3, GripVertical, RotateCcw } from "lucide-react";

// "Columns" toolbar button + panel: show/hide, order (drag or arrows), reset
export function ColumnsPanel({ layout, labels, onChange, onReset, testid }) {
  const [open, setOpen] = useState(false);
  const [drag, setDrag] = useState(null);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) { return undefined; }
    const off = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); } };
    const esc = (e) => { if (e.key === "Escape") { setOpen(false); } };
    document.addEventListener("mousedown", off);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", off); document.removeEventListener("keydown", esc); };
  }, [open]);
  const shown = layout.filter((c) => c.visible).length;
  const move = (from, to) => {
    if (to < 0 || to >= layout.length || from === to) return;
    const n = [...layout];
    const [x] = n.splice(from, 1);
    n.splice(to, 0, x);
    onChange(n);
  };
  const flip = (i) => {
    if (layout[i].visible && shown <= 1) return;
    onChange(layout.map((c, j) => (j === i ? { ...c, visible: !c.visible } : c)));
  };
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(!open)} data-testid={`${testid}-columns-btn`}
        className="inline-flex h-8 items-center gap-1 rounded border border-slate-200 bg-white px-2.5 text-xs font-medium text-slate-700 hover:bg-slate-100"><Columns3 className="h-3.5 w-3.5" />Columns</button>
      {open && (
        <div className="fixed inset-x-3 top-24 z-50 rounded-md border border-slate-200 bg-white p-2 shadow-lg md:absolute md:inset-x-auto md:right-0 md:top-auto md:mt-1 md:w-72" data-testid={`${testid}-columns-panel`}>
          <div className="mb-1 flex items-center justify-between px-1">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Show / order columns</span>
            <button className="inline-flex items-center gap-1 text-[11px] text-sky-700 hover:underline" onClick={onReset} data-testid={`${testid}-columns-reset`}><RotateCcw className="h-3 w-3" />Reset to default</button>
          </div>
          <ul className="max-h-80 overflow-y-auto">
            {layout.map((c, i) => (
              <li key={c.key} draggable onDragStart={(e) => { e.dataTransfer.setData("text/plain", c.key); setDrag(i); }} onDragOver={(e) => e.preventDefault()} onDrop={() => { move(drag, i); setDrag(null); }}
                className={`flex items-center gap-1 rounded px-1 py-1 text-xs hover:bg-slate-50 ${drag === i ? "opacity-50" : ""}`} data-testid={`${testid}-col-item-${c.key}`}>
                <GripVertical className="h-3.5 w-3.5 shrink-0 cursor-grab text-slate-400" />
                <label className="flex flex-1 cursor-pointer items-center gap-2 truncate">
                  <input type="checkbox" className="h-3.5 w-3.5 accent-sky-700" checked={c.visible} disabled={c.visible && shown <= 1} onChange={() => flip(i)} data-testid={`${testid}-col-toggle-${c.key}`} />
                  <span className="truncate">{labels[c.key] || c.key}</span>
                </label>
                <button className="rounded p-0.5 hover:bg-slate-200 disabled:opacity-30" disabled={i === 0} onClick={() => move(i, i - 1)} data-testid={`${testid}-col-up-${c.key}`}><ArrowUp className="h-3 w-3" /></button>
                <button className="rounded p-0.5 hover:bg-slate-200 disabled:opacity-30" disabled={i === layout.length - 1} onClick={() => move(i, i + 1)} data-testid={`${testid}-col-down-${c.key}`}><ArrowDown className="h-3 w-3" /></button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
